import { useState, useEffect, useContext } from 'react';
import api from './api/axiosInstance';
import { UserContext } from './UserContext';
import { Link } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Sidebar from './Sidebar.js';
import { Form, Row, Col, Button, Card, Table, Badge, Image } from 'react-bootstrap';
import TicketFilter from './TicketFilter';
import Pagination from 'react-bootstrap/Pagination';
import Breadcrumb from './Breadcrumb';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { isAdminType} from './utils/roleUtils';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import icNoTickets from './assets/img/ic-empty-box.svg';

const TicketList = () => {
    const [tickets, setTickets] = useState([]);
    const [filteredTickets, setFilteredTickets] = useState([]);
    const [priorityFilter, setPriorityFilter] = useState('');
    
    const [ticketsPerPage, setTicketsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const indexOfLastTicket = currentPage * ticketsPerPage;
    const indexOfFirstTicket = indexOfLastTicket - ticketsPerPage;
    const currentTickets = filteredTickets.slice(indexOfFirstTicket, indexOfLastTicket);
    const [sortOrderId, setSortOrderId] = useState(null);
    const [sortOrderPriority, setSortOrderPriority] = useState(null);
    const [sortOrderClass, setSortOrderClass] = useState(null);
    const [sortOrderOwner, setSortOrderOwner] = useState(null);
    const [sortOrderCreated, setSortOrderCreated] = useState(null);
    const [sortOrderUpdated, setSortOrderUpdated] = useState(null);
    const [sortOrderStatusText, setSortOrderStatusText] = useState(null);
    const [sortOrderActivity, setSortOrderActivity] = useState(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [ticketClassFilter, setTicketClassFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('')

    const [owners, setOwners] = useState([]);
    const [selectedOwner, setSelectedOwner] = useState('');
    const { user } = useContext(UserContext);
    const [selectedTicket] = useState('');
    const [showArchived, setShowArchived] = useState(false); // ✅ Toggle between archived and unarchived

    const [bulkMode, setBulkMode] = useState(false); // ✅ To toggle checkbox mode
    const [selectedTickets, setSelectedTickets] = useState([]); // ✅ Store selected ticket IDs

    const STATUS_BG = { Resolved: 'info', Open: 'active', Archived: 'inactive' };

    useEffect(() => {
        if (user && isAdminType(user.role)) {
            fetchTickets();
        }
    }, [user]);

    const fetchTickets = async (archived = false) => {
        try {
            const res = await api.get(`/api/tickets`);
    
            const filtered = res.data.filter(ticket =>
                archived ? ticket.status === 'Archived' : ticket.status !== 'Archived'
            );
            
            setTickets(filtered);
            setFilteredTickets(filtered);
        } catch (error) {
            console.error('Error fetching tickets:', error);
        }
    };
    
    const handleToggleArchive = () => {
        const nextView = !showArchived;
        setShowArchived(nextView);
        fetchTickets(nextView); // ✅ pass true for archived
    };

    // Handle search filter
    useEffect(() => {
        const lowercasedQuery = searchQuery.toLowerCase();
    
        const filteredResults = tickets.filter((ticket) =>
            ticket.businessPurpose.toLowerCase().includes(lowercasedQuery) &&
            (selectedTicket === '' || ticket.id === selectedTicket) &&
            (priorityFilter === '' || ticket.priority === priorityFilter) &&
            (selectedOwner === '' || ticket.owner?._id === selectedOwner) &&
            (ticketClassFilter === '' || ticket.ticketClass === ticketClassFilter) &&
            (statusFilter === '' || ticket.status === statusFilter)
        );
    
        setFilteredTickets(filteredResults);
        setCurrentPage(1);
    }, [searchQuery, selectedTicket, tickets, priorityFilter, selectedOwner, ticketClassFilter, statusFilter]);

    // Handle owner filter
    useEffect(() => {
        const fetchOwners = async () => {
            try {
                const res = await api.get('/api/users/platform-admins');

                const admins = Array.isArray(res.data) ? res.data : [];

                setOwners(
                    admins.map(u => ({
                        id: u._id,
                        label: `${u.firstName || ''} ${u.familyName || ''}`.trim(),
                    }))
                );
            } catch (err) {
                console.error('Error fetching owners:', err);
            }
        };
        fetchOwners();
    }, []);
    

    // Sorting function for Ticket ID
    const sortTicketsById = () => {
        const next = sortOrderId === 'asc' ? 'desc' : 'asc';
        setSortOrderId(next);

        setFilteredTickets(prev => {
            const toNum = v => {
            const n = parseInt(String(v ?? '').replace(/\D+/g, ''), 10);
            return Number.isNaN(n) ? null : n;
            };

            const arr = [...prev];
            arr.sort((a, b) => {
            const A = toNum(a.ticketId);
            const B = toNum(b.ticketId);

            // Put non-numeric IDs last in asc, first in desc (tweak if you prefer)
            if (A === null && B === null) return 0;
            if (A === null) return next === 'asc' ? 1 : -1;
            if (B === null) return next === 'asc' ? -1 : 1;

            return next === 'asc' ? A - B : B - A;
            });
            return arr;
        });
    };


    const PRIORITY_RANK = { Critical: 4, High: 3, Medium: 2, Low: 1 };

    const sortTicketsByPriority = () => {
        const next = sortOrderPriority === 'asc' ? 'desc' : 'asc';
        setSortOrderPriority(next);

        setFilteredTickets(prev => {
            const rank = p => PRIORITY_RANK[p] ?? 0;

            const arr = [...prev];
            arr.sort((a, b) => {
                const A = rank(a?.priority);
                const B = rank(b?.priority);

                if (A !== 0 || B !== 0) {
                    return next === 'asc' ? A - B : B - A; // asc: Low→Critical, desc: Critical→Low
                }

                // Fallback to alpha if unknown values
                const pa = (a?.priority ?? '').toString().toLowerCase();
                const pb = (b?.priority ?? '').toString().toLowerCase();
                return next === 'asc' ? pa.localeCompare(pb) : pb.localeCompare(pa);
            });

            return arr;
        });
    };


    // Sorting function for Ticket class
    const CLASS_RANK = {
        'Incident': 1,
        'Support req.': 2,
        'Advisory req.': 3,
        'Lifecycle task': 4,
        'Special task': 5,
    };

    const sortTicketsByClass = () => {
        const next = sortOrderClass === 'asc' ? 'desc' : 'asc';
        setSortOrderClass(next);

        setFilteredTickets(prev => {
            const rank = c => CLASS_RANK[c] ?? Number.POSITIVE_INFINITY; // unknowns last
            const arr = [...prev];
            arr.sort((a, b) => {
                const A = rank(a?.ticketClass);
                const B = rank(b?.ticketClass);
                return next === 'asc' ? A - B : B - A;
            });
            return arr;
        });
    };


    // Sorting function for Ticket owner
    const sortTicketsByOwner = () => {
        const next = sortOrderOwner === 'asc' ? 'desc' : 'asc';
        setSortOrderOwner(next);

        setFilteredTickets(prev => {
            const arr = [...prev];
            arr.sort((a, b) => {
                const A = `${a?.owner?.firstName || ''} ${a?.owner?.familyName || ''}`.trim().toLowerCase();
                const B = `${b?.owner?.firstName || ''} ${b?.owner?.familyName || ''}`.trim().toLowerCase();

                // Put empty owners last in asc, first in desc
                if (!A && !B) return 0;
                if (!A) return next === 'asc' ? 1 : -1;
                if (!B) return next === 'asc' ? -1 : 1;

                return next === 'asc' ? A.localeCompare(B) : B.localeCompare(A);
            });
            return arr;
        });
    };

    
    // Sorting tickets - last update
    const sortTicketsByUpdatedAt = () => {
        const next = sortOrderUpdated === 'asc' ? 'desc' : 'asc';
        setSortOrderUpdated(next);

        setFilteredTickets(prev => {
            const arr = [...prev];
            arr.sort((a, b) => {
                const A = Date.parse(a?.updatedAt);
                const B = Date.parse(b?.updatedAt);
                const aValid = Number.isFinite(A);
                const bValid = Number.isFinite(B);

                // put missing/invalid dates last in asc, first in desc
                if (!aValid && !bValid) return 0;
                if (!aValid) return next === 'asc' ? 1 : -1;
                if (!bValid) return next === 'asc' ? -1 : 1;

                return next === 'asc' ? A - B : B - A;
            });
            return arr;
        });
    };

    // Sorting tickets by time created
    const sortTicketsByCreatedAt = () => {
        const next = sortOrderCreated === 'asc' ? 'desc' : 'asc';
        setSortOrderCreated(next);

        setFilteredTickets(prev => {
            const arr = [...prev];
            arr.sort((a, b) => {
                const A = Date.parse(a?.createdAt);
                const B = Date.parse(b?.createdAt);
                const aValid = Number.isFinite(A);
                const bValid = Number.isFinite(B);

                // Put invalid/missing dates last in asc, first in desc
                if (!aValid && !bValid) return 0;
                if (!aValid) return next === 'asc' ? 1 : -1;
                if (!bValid) return next === 'asc' ? -1 : 1;

                return next === 'asc' ? A - B : B - A;
            });
            return arr;
        });
    };


    // Sorting tickets by status
    const STATUS_RANK = { Open: 3, Resolved: 2, Archived: 1 }; // tweak as needed

    const sortTicketsByStatusText = () => {
        const next = sortOrderStatusText === 'asc' ? 'desc' : 'asc';
        setSortOrderStatusText(next);

        setFilteredTickets(prev => {
                const rank = s => STATUS_RANK[s] ?? 0;
                const arr = [...prev];
                arr.sort((a, b) => {
                const A = rank(a?.status);
                const B = rank(b?.status);
                return next === 'asc' ? A - B : B - A; // asc: low->high, desc: high->low
            });
            return arr;
        });
    };
    
    // Sorting tickets by activities
    const sortTicketsByActivityCount = () => {
        const next = sortOrderActivity === 'asc' ? 'desc' : 'asc';
        setSortOrderActivity(next);

        setFilteredTickets(prev => {
            const asCount = v =>
            Array.isArray(v) ? v.length :
            Number.isFinite(Number(v)) ? Number(v) : 0;

            const arr = [...prev];
            arr.sort((a, b) => {
                const A = asCount(a?.activity);
                const B = asCount(b?.activity);
                return next === 'asc' ? A - B : B - A;
            });
            return arr;
        });
    };
    
    const getTimeDifference = (createdAt) => {
        const created = new Date(createdAt);
        const now = new Date();
        const diffMs = now - created;
    
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffHours / 24);
        const remainingHours = diffHours % 24;
    
        return `${diffDays}d ${remainingHours}h`;
    };
    
    
    if (!user || !isAdminType(user.role)) {
        return <div>You are not authorized to view this page.</div>;
    }

    const totalPages = Math.ceil(filteredTickets.length / ticketsPerPage);

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    const handleBulkSelect = () => {
        setBulkMode(!bulkMode);
        setSelectedTickets([]); // Clear on toggle
    };

    const handleBulkArchiveToggle = async () => {
        if (selectedTickets.length === 0) {
            alert(`No tickets selected to ${showArchived ? 'unarchive' : 'archive'}.`);
            return;
        }
    
        try {
            await Promise.all(
                selectedTickets.map((ticketId) =>
                    api.put(
                        `/api/tickets/${ticketId}`,
                        { status: showArchived ? 'Open' : 'Archived' }
                    )
                )
            );
    
            fetchTickets(showArchived); // ✅ Refresh the current view
            setSelectedTickets([]);
            setBulkMode(false);
        } catch (error) {
            console.error(`Error ${showArchived ? 'unarchiving' : 'archiving'} tickets:`, error);
            alert(`An error occurred while trying to ${showArchived ? 'unarchive' : 'archive'} tickets.`);
        }
    };

    const handleExportTask = () => {
        if (!user?.firstName || !user?.familyName) return;
    
        const fullName = `${user.firstName} ${user.familyName}`.replace(/\s+/g, ' ').trim();
        const normalizedFullName = fullName.toLowerCase();
    
        const myTickets = filteredTickets.filter(ticket => ticket.owner?._id === user?._id); // ✅
    
        if (myTickets.length === 0) return;
    
        const doc = new jsPDF();
        const today = new Date().toLocaleDateString('de-DE');
    
        // Header
        doc.setFontSize(16);
        doc.text(`Tasks for ${fullName}`, 14, 20);
        doc.setFontSize(10);
        doc.text(`Exported on: ${today}`, 150, 20, { align: 'right' });
    
        // Table
        autoTable(doc, {
            head: [['Ticket ID', 'Priority', 'Class', 'Status', 'Business Purpose', 'Updated']],
            body: myTickets.map(t => [
                t.ticketId,
                t.priority,
                t.ticketClass,
                t.status,
                t.businessPurpose || '',
                new Date(t.updatedAt).toLocaleDateString('de-DE'),
            ]),
            startY: 30,
            styles: { fontSize: 10 },
            didDrawPage: function (data) {
                // Add page number at bottom center
                const pageCount = doc.internal.getNumberOfPages();
                const pageSize = doc.internal.pageSize;
                const pageHeight = pageSize.height || pageSize.getHeight();
                doc.setFontSize(8);
                doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`, pageSize.width / 2, pageHeight - 10, { align: 'center' });
            }
        });
    
        doc.save(`tasks_${fullName.replace(/\s+/g, '_')}.pdf`);
    };      
    
    const noTickets = filteredTickets.length === 0;
    
    return (
        <>
            <Sidebar ticketCount={tickets.length} />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Card>
                                <Card.Body>
                                    <Row>
                                        <Col xs={6}>
                                            <h1>Tickets</h1>
                                        </Col>
                                        
                                        <Col md={6} className="text-end">
                                            {bulkMode && (
                                                <Button
                                                    onClick={handleBulkArchiveToggle}
                                                    variant="primary"
                                                     className="btn-sm me-2"
                                                    disabled={selectedTickets.length === 0} // ✅ Disable if nothing selected
                                                >
                                                    {showArchived ? 'Unarchive selected tickets' : 'Archive selected tickets'}
                                                </Button>
                                            )}
                                            {!bulkMode ? (
                                                <Button onClick={handleBulkSelect} variant="outline-secondary" className="btn-sm">
                                                    Bulk Select
                                                </Button>
                                            ) : (
                                                <Button onClick={handleBulkSelect} variant="outline-secondary" className="btn-sm">
                                                    Cancel
                                                </Button>
                                            )}
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xs={12}>
                                            <TicketFilter
                                                searchQuery={searchQuery}
                                                setSearchQuery={setSearchQuery}
                                                tickets={tickets}                                
                                                priorityFilter={priorityFilter} // ✅
                                                setPriorityFilter={setPriorityFilter} // ✅
                                                selectedOwner={selectedOwner}
                                                setSelectedOwner={setSelectedOwner}
                                                owners={owners}
                                                ticketClassFilter={ticketClassFilter}
                                                setTicketClassFilter={setTicketClassFilter}
                                                statusFilter={statusFilter}
                                                setStatusFilter={setStatusFilter}
                                            />
                                            {noTickets ? (
                                                <Row>
                                                    <Col md={12}>
                                                        <Row className="mb-3">
                                                            <Col md={6} className="no-result offset-md-3 d-flex flex-column justify-content-center align-items-center text-center">
                                                                <Image src={icNoTickets} alt="No tickets" className="mb-4" />
                                                                <h2 className="mb-3">No tickets yet.</h2>
                                                                <p className="mb-4">Your tickets will be displayed here.</p>
                                                                <Button as={Link} to="/ticket-add" variant="primary">Add Ticket</Button>
                                                            </Col>
                                                        </Row>
                                                    </Col>
                                                </Row>
                                            ) : (
                                                <Row>
                                                    <Col>
                                                        <div className="table-responsive list">
                                                            <Table>
                                                                <thead>
                                                                    <tr>
                                                                        {bulkMode && (
                                                                            <th>
                                                                                <Form.Check
                                                                                    type="checkbox"
                                                                                    onChange={(e) => {
                                                                                        if (e.target.checked) {
                                                                                            setSelectedTickets(filteredTickets.map(t => t._id));
                                                                                        } else {
                                                                                            setSelectedTickets([]);
                                                                                        }
                                                                                    }}
                                                                                    checked={selectedTickets.length === filteredTickets.length && filteredTickets.length > 0}
                                                                                    style={{ marginRight: '4px' }}
                                                                                />
                                                                            </th>
                                                                        )}
                                                                        <th className="th-fixed-id" onClick={sortTicketsById} style={{ cursor: 'pointer' }}>
                                                                            <div>
                                                                                ID
                                                                                <span>
                                                                                    <FaCaretUp   className={sortOrderId === 'asc'  ? 'text-primary' : ''} />
                                                                                    <FaCaretDown className={sortOrderId === 'desc' ? 'text-primary' : ''} />
                                                                                </span>
                                                                            </div>
                                                                        </th>
                                                                        <th
                                                                            className="th-fixed-priority"
                                                                            onClick={sortTicketsByPriority}
                                                                            style={{ cursor: 'pointer' }}
                                                                        >
                                                                            <div>
                                                                                Priority
                                                                                <span>
                                                                                    <FaCaretUp   className={sortOrderPriority === 'asc'  ? 'text-primary' : ''} />
                                                                                    <FaCaretDown className={sortOrderPriority === 'desc' ? 'text-primary' : ''} />
                                                                                </span>
                                                                            </div>
                                                                        </th>
                                                                        <th className="th-fixed-class" onClick={sortTicketsByClass} style={{ cursor: 'pointer' }}>
                                                                            <div>
                                                                                Class
                                                                                <span>
                                                                                    <FaCaretUp   className={sortOrderClass === 'asc'  ? 'text-primary' : ''} />
                                                                                    <FaCaretDown className={sortOrderClass === 'desc' ? 'text-primary' : ''} />
                                                                                </span>
                                                                            </div>
                                                                        </th>
                                                                        <th className="th-fixed-created-by" onClick={sortTicketsByOwner} style={{ cursor: 'pointer' }}>
                                                                            <div>
                                                                                Created by
                                                                                <span>
                                                                                    <FaCaretUp   className={sortOrderOwner === 'asc'  ? 'text-primary' : ''} />
                                                                                    <FaCaretDown className={sortOrderOwner === 'desc' ? 'text-primary' : ''} />
                                                                                </span>
                                                                            </div>
                                                                        </th>
                                                                        <th className="th-fixed-owner" onClick={sortTicketsByOwner} style={{ cursor: 'pointer' }}>
                                                                            <div>
                                                                                Owner
                                                                                <span>
                                                                                    <FaCaretUp   className={sortOrderOwner === 'asc'  ? 'text-primary' : ''} />
                                                                                    <FaCaretDown className={sortOrderOwner === 'desc' ? 'text-primary' : ''} />
                                                                                </span>
                                                                            </div>
                                                                        </th>
                                                                        <th className="th-fixed-updated-by" onClick={sortTicketsByUpdatedAt} style={{ cursor: 'pointer' }}>
                                                                            <div>
                                                                                Update
                                                                                <span>
                                                                                    <FaCaretUp   className={sortOrderUpdated === 'asc'  ? 'text-primary' : ''} />
                                                                                    <FaCaretDown className={sortOrderUpdated === 'desc' ? 'text-primary' : ''} />
                                                                                </span>
                                                                            </div>
                                                                        </th>
                                                                        <th className="th-fixed-open-since" onClick={sortTicketsByCreatedAt} style={{ cursor: 'pointer' }}>
                                                                            <div>
                                                                                Open Since
                                                                                <span>
                                                                                    <FaCaretUp   className={sortOrderCreated === 'asc'  ? 'text-primary' : ''} />
                                                                                    <FaCaretDown className={sortOrderCreated === 'desc' ? 'text-primary' : ''} />
                                                                                </span>
                                                                            </div>
                                                                        </th>
                                                                        <th className="th-fixed-status" onClick={sortTicketsByStatusText} style={{ cursor: 'pointer' }}>
                                                                            <div>
                                                                                Status
                                                                                <span>
                                                                                    <FaCaretUp   className={sortOrderStatusText === 'asc'  ? 'text-primary' : ''} />
                                                                                    <FaCaretDown className={sortOrderStatusText === 'desc' ? 'text-primary' : ''} />
                                                                                </span>
                                                                            </div>
                                                                        </th>
                                                                        <th className="th-fixed-activity" onClick={sortTicketsByActivityCount} style={{ cursor: 'pointer' }}>
                                                                            <div>
                                                                                Activity
                                                                                <span>
                                                                                    <FaCaretUp   className={sortOrderActivity === 'asc'  ? 'text-primary' : ''} />
                                                                                    <FaCaretDown className={sortOrderActivity === 'desc' ? 'text-primary' : ''} />
                                                                                </span>
                                                                            </div>
                                                                        </th>
                                                                        <th className="th-fixed-actions"></th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {currentTickets.map((ticket) => (
                                                                        <tr key={ticket._id} style={{ cursor: 'pointer' }}>
                                                                            {bulkMode && (
                                                                                <td>
                                                                                    <Form.Check
                                                                                        type="checkbox"
                                                                                        checked={selectedTickets.includes(ticket._id)}
                                                                                        onChange={(e) => {
                                                                                            if (e.target.checked) {
                                                                                                setSelectedTickets(prev => [...prev, ticket._id]);
                                                                                            } else {
                                                                                                setSelectedTickets(prev => prev.filter(id => id !== ticket._id));
                                                                                            }
                                                                                        }}
                                                                                        style={{ marginRight: '6px' }}
                                                                                    />
                                                                                </td>          
                                                                            )}
                                                                            <td>#{ticket.ticketId}</td>
                                                                            <td>
                                                                                {(() => {
                                                                                    const key = String(ticket?.priority ?? '').trim().toLowerCase();
                                                                                    const allowed = ['critical', 'high', 'medium', 'low'];
                                                                                    if (!allowed.includes(key)) return ticket?.priority || '—';

                                                                                    const label = key.charAt(0).toUpperCase() + key.slice(1);
                                                                                    return <span className={`badge rounded-pill ${key}`}>{label}</span>;
                                                                                })()}
                                                                            </td>

                                                                            <td>{ticket.ticketClass}</td>
                                                                            <td>
                                                                                {new Date(ticket.createdAt).toLocaleDateString('de-DE')}
                                                                                <br />
                                                                                {ticket.createdBy
                                                                                    ? `${ticket.createdBy.firstName ?? ''} ${ticket.createdBy.familyName ?? ''}`.trim() ||
                                                                                    ticket.createdBy.email
                                                                                    : 'N/A'
                                                                                }
                                                                            </td>
                                                                            <td>
                                                                                {ticket.owner
                                                                                    ? `${ticket.owner.firstName || ''} ${ticket.owner.familyName || ''}`.trim()
                                                                                    : '—'}
                                                                            </td>
                                                                            <td>
                                                                                {ticket.updatedAt && (
                                                                                    <span>{new Date(ticket.updatedAt).toLocaleDateString('de-DE')}.</span>
                                                                                )}
                                                                                <br />
                                                                                {ticket.updatedBy
                                                                                    ? `${ticket.updatedBy.firstName ?? ''} ${ticket.updatedBy.familyName ?? ''}`.trim() ||
                                                                                    ticket.updatedBy.email
                                                                                    : 'N/A'
                                                                                }
                                                                            </td>
                                                                            <td>{ticket.createdAt && (
                                                                                <span>{getTimeDifference(ticket.createdAt)}</span>
                                                                            )}</td>
                                                                            <td>
                                                                                <OverlayTrigger
                                                                                    placement="top"
                                                                                    overlay={
                                                                                        <Tooltip id={`tooltip-status-${ticket._id}`}>{ticket.status}</Tooltip>
                                                                                    }
                                                                                >
                                                                                    <Badge bg={STATUS_BG[ticket.status] ?? 'light'}>&nbsp;</Badge>
                                                                                </OverlayTrigger>
                                                                            </td>
                                                                            <td>{Array.isArray(ticket.activity) ? ticket.activity.length : 0}</td>
                                                                            {!bulkMode && (
                                                                                <td className="justify-content-end d-flex align-items-center">
                                                                                    <Link to={`/ticket-edit/${ticket._id}`} className="btn btn-sm btn-outline-secondary">
                                                                                        View
                                                                                    </Link>
                                                                                </td>
                                                                            )}
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </Table>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            )}
                                            {/* Pagination Row with Total Count, Items Per Page Selector & "Go to Page" Input */}
                                            <Row className="pagination-wrapper">
                                                <Col className="d-flex align-items-center">
                                                    <span>Total {filteredTickets.length} Tickets </span>
                                                    <Pagination>
                                                        <Pagination.Prev 
                                                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                                                            disabled={currentPage === 1}
                                                        />
                                                        {[...Array(totalPages).keys()].map(number => (
                                                            <Pagination.Item 
                                                                key={number + 1} 
                                                                active={number + 1 === currentPage} 
                                                                onClick={() => handlePageChange(number + 1)}
                                                            >
                                                                {number + 1}
                                                            </Pagination.Item>
                                                        ))}
                                                        <Pagination.Next 
                                                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
                                                            disabled={currentPage === totalPages}
                                                        />
                                                    </Pagination>
                                                </Col>
                                                <Col className="pagination-controls">
                                                    <div className="pagination-controls-per-page">
                                                        <Form.Select
                                                            value={ticketsPerPage}
                                                            onChange={(e) => setTicketsPerPage(Number(e.target.value))} // ✅ Updates state
                                                            style={{ width: 'auto', marginRight: '10px' }}
                                                        >
                                                            <option value="10">10 / page</option>
                                                            <option value="20">20 / page</option>
                                                            <option value="40">40 / page</option>
                                                        </Form.Select>
                                                    </div>
                                                    <label className="pagination-controls-goto-label" htmlFor="goto-page">
                                                        Go to
                                                    </label>
                                                    <Form.Control
                                                        type="number"
                                                        className="pagination-controls-goto-input"
                                                        value={currentPage}
                                                        onChange={(e) => {
                                                            const newPage = Number(e.target.value);
                                                            if (newPage >= 1 && newPage <= totalPages) {
                                                                setCurrentPage(newPage); // ✅ Only valid pages
                                                            }
                                                        }}
                                                        onBlur={() => {
                                                            if (currentPage < 1) setCurrentPage(1);
                                                            if (currentPage > totalPages) setCurrentPage(totalPages);
                                                        }}
                                                    />
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card> 
                        </Col>
                    </Row>
                    <Row className="buttons-row">
                        <Col>
                            <Button onClick={handleToggleArchive} variant="outline-secondary">
                                {showArchived ? 'See active tickets' : 'See archived tickets'}
                            </Button>
                            {/* <Button onClick={handleExportTask} variant="outline-secondary">
                                Export my tasks as PDF
                            </Button> */}
                            <Link to="/ticket-add" className="btn btn-outline-primary">
                                Create new ticket
                            </Link>
                        </Col>
                    </Row>
                </Container>
            </main>
        </>
    );
};

export default TicketList;
